import { getTheme } from '@fluentui/react';
import { M365DarkTheme } from './M365DarkTheme';
import { M365LightTheme } from './M365LightTheme';
import { IM365Theme } from './M365Theme.types';
import { TeamsDarkCustomizationsWithStyles } from './TeamsDarkTheme';
import { TeamsLightCustomizationsWithStyles } from './TeamsLightTheme';
import { M365ThemeKind } from './theme.types';
import { M365Styles } from '.';

  // fabric default theme
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fabricDefaultTheme: IM365Theme = getTheme() as any;

  fabricDefaultTheme.semanticColors = M365LightTheme.settings.theme.semanticColors;

  export interface IProductIconColors {
    bing: string
    bookings: string
    docs: string
    dynamics: string
    exchange: string
    kaizala: string
    office: string
    forms: string
    oneDrive: string
    outlook: string
    planner: string
    powerApps: string
    powerAutomate: string
    powerBi: string
    project: string
    sharepoint: string
    skype: string
    staffhub: string
    stream: string
    sway: string
    teams: string
    yammer: string
    windows: string
  }

  export const productIconsLightColors: IProductIconColors = {
    bing: '#028273',
    bookings: '#00A99D',
    docs: '#D83B01',
    dynamics: '#2266E3',
    exchange: '#0078D4',
    kaizala: '#0178D6',
    forms: '#008273',
    office: '#D83B01',
    oneDrive: '#0078D4',
    outlook: '#006EC3',
    planner: '#107C41',
    powerApps: '#742774',
    powerAutomate: '#0066FF',
    powerBi: '#DAB50F',
    project: '#107C41',
    sharepoint: '#03787C',
    skype: '#0078D4',
    staffhub: '#55C4C1',
    stream: '#BC1948',
    sway: '#008272',
    teams: '#5b5fc7',
    yammer: '#106EBE',
    windows: '#0078D7',
  };

  const productIconsDarkColors: IProductIconColors = {
    ...productIconsLightColors,
    dynamics: '#4083FF',
    office: '#F7894A',
    planner: '#10893C',
    powerApps: '#D98FD9',
    project: '#10893C',
    sharepoint: '#009CA4',
    stream: '#E8467C',
    teams: '#7A7FEA',
  };

  // this is m365's extension to fabric's theme variables
  export interface IM365ExtensionSlots {
    semanticColors: {
      contrastBackground: string
      dashboardSearchBoxHoverBackground: string
      // Add color slot for recommendation tag. later this will move to admin controls
      recommendationTag: string
      productIcons: IProductIconColors
      topActionCardThemeColor: string
      topActionCardBackgroundColor: string
      themeIconVariant: string
    }
    sizing: {
      largeModalWidth: number
      cloudShellDefaultHeight: number
    }
  }

  const defaultSizing: IM365ExtensionSlots['sizing'] = {
    largeModalWidth: 640,
    cloudShellDefaultHeight: 500,
  };

  const defaultTheme: IM365AppTheme = {
    ...fabricDefaultTheme,
    m365: {
      semanticColors: {
        contrastBackground: '',
        dashboardSearchBoxHoverBackground: '',
        recommendationTag: '',
        productIcons: productIconsLightColors,
        topActionCardThemeColor: fabricDefaultTheme.palette.themePrimary,
        topActionCardBackgroundColor: fabricDefaultTheme.semanticColors.bodyBackground,
        themeIconVariant: 'FabricDefault',
      },
      sizing: defaultSizing,
    },
  };

  const lightTheme: IM365AppTheme = {
    ...M365LightTheme.settings.theme,
    m365: {
      semanticColors: {
        contrastBackground: fabricDefaultTheme.palette.neutralLighter,
        dashboardSearchBoxHoverBackground: fabricDefaultTheme.palette.white,
        recommendationTag: '#D3F4ED',
        productIcons: productIconsLightColors,
        topActionCardThemeColor: M365LightTheme.settings.theme.palette.themePrimary,
        topActionCardBackgroundColor: M365LightTheme.settings.theme.semanticColors.bodyBackground,
        themeIconVariant: 'M365Light',
      },
      sizing: defaultSizing,
    },
  };

  const lightWithContrastTheme: IM365AppTheme = {
    ...lightTheme,
    m365: {
      ...lightTheme.m365,
      semanticColors: {
        ...lightTheme.m365.semanticColors,
        contrastBackground: '#E9E9E9',
      },
    },
  };

  const darkTheme: IM365AppTheme = {
    ...M365DarkTheme.settings.theme,
    m365: {
      semanticColors: {
        contrastBackground: M365DarkTheme.settings.theme.palette.white,
        dashboardSearchBoxHoverBackground: '#4D4D4D',
        recommendationTag: '#0C614C',
        productIcons: productIconsDarkColors,
        topActionCardThemeColor: M365DarkTheme.settings.theme.palette.themePrimary,
        topActionCardBackgroundColor: M365DarkTheme.settings.theme.semanticColors.bodyBackground,
        themeIconVariant: 'M365Dark',
      },
      sizing: defaultSizing,
    },
  };

  const teamsLightTheme: IM365AppTheme = {
    ...TeamsLightCustomizationsWithStyles.settings.theme,
    m365: {
      semanticColors: {
        contrastBackground: fabricDefaultTheme.palette.neutralLighter,
        dashboardSearchBoxHoverBackground: fabricDefaultTheme.palette.white,
        recommendationTag: '#D3F4ED',
        productIcons: productIconsLightColors,
        topActionCardThemeColor: TeamsLightCustomizationsWithStyles.settings.theme.palette.themePrimary,
        topActionCardBackgroundColor: M365LightTheme.settings.theme.semanticColors.bodyBackground,
        themeIconVariant: 'TeamsLight',
      },
      sizing: defaultSizing,
    },
  };

  const teamsDarkTheme: IM365AppTheme = {
    ...TeamsDarkCustomizationsWithStyles.settings.theme,
    m365: {
      semanticColors: {
        contrastBackground: TeamsDarkCustomizationsWithStyles.settings.theme.palette.white,
        dashboardSearchBoxHoverBackground: '#4D4D4D',
        recommendationTag: '#0C614C',
        productIcons: productIconsDarkColors,
        topActionCardThemeColor: TeamsDarkCustomizationsWithStyles.settings.theme.palette.themePrimary,
        topActionCardBackgroundColor: M365DarkTheme.settings.theme.semanticColors.bodyBackground,
        themeIconVariant: 'TeamsDark',
      },
      sizing: defaultSizing,
    },
  };

  export const getM365Theme = (themeKind: M365ThemeKind): IM365AppTheme => {
    switch (themeKind) {
      case M365ThemeKind.FabricDefaultTheme:
        return defaultTheme;
      case M365ThemeKind.M365Light:
        return lightTheme;
      case M365ThemeKind.M365LightWithContrast:
        return lightWithContrastTheme;
      case M365ThemeKind.M365Dark: {
        return darkTheme;
      }
      case M365ThemeKind.TeamsLight:
        return teamsLightTheme;
      case M365ThemeKind.TeamsDark:
        return teamsDarkTheme;
    }
  };

  // No type safe solution for global styles yet
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const M365AppStyles: any = {
    ...M365Styles,
  };

  export const getScopedSettings = (theme: M365ThemeKind) => {
    if (theme === M365ThemeKind.TeamsDark) {
      return { ...TeamsDarkCustomizationsWithStyles.scopedSettings };
    }
    if (theme === M365ThemeKind.TeamsLight) {
      return {
        ...TeamsLightCustomizationsWithStyles.scopedSettings,
      };
    }
    return M365AppStyles;
  };

  /**
   * Theme variables with m365 extensions
   */
  export type IM365AppTheme = IM365Theme & { m365: IM365ExtensionSlots }

  /**
   * Interface for a prop set that supports theming
   */
  export interface IThemedProp {
    theme: IM365AppTheme
  }
